import React from 'react';
import { useNavigate } from 'react-router-dom';
import JoinPromptDialog from '@components/JoinPromptDialog.jsx';
import QuestionGameSummary from './QuestionGameSummary.jsx';
import QuestionGameSummaryMobile from './QuestionGameSummaryMobile.jsx';
import Countdown from '../components/Countdown.jsx';
import QuestionRenderer from '../components/QuestionRenderer.jsx';
import AnswerGrid from '../components/AnswerGrid.jsx';
import Scoreboard from '../components/Scoreboard.jsx';
import { UseQuestionGameController } from '../controllers/useQuestionGameController.jsx';
import '../styles/QuestionGameView.css';

export default function QuestionGameView() {
    const navigate = useNavigate();
    const controller = UseQuestionGameController();
    const {
        lobbyCode, isLoading, questions, currentIndex, scoreboard,
        isCreator, isSpectator, timeLeft, initialCountdown,
        countdown, gameOver, showFinalScreen, setShowFinalScreen,
        showJoinDialog, joinError, forcePassword, selected,
        revealed, correct, progress, handleJoinDialogSubmit,
        selectAnswer, nextQuestion, handleLeaveLobby, handleReturnToLobby
    } = controller;

    const isMobile = window.innerWidth <= 500;
    if (isLoading) return null;
    if (initialCountdown) return <p>Spiel startet in {countdown}s…</p>;

    if (!questions.length) {
        return showJoinDialog ? (
            <JoinPromptDialog
                lobbyCode={lobbyCode}
                onSubmit={handleJoinDialogSubmit}
                onClose={() => navigate('/')}
                error={joinError}
                forcePassword={forcePassword}
            />
        ) : (
            <div className="loading-container">
                <p>Warten auf Spielstart…</p>
                {isSpectator && <span className="spectator-hint">Du bist im Zuschauer-Modus</span>}
            </div>
        );
    }

    const topPlayers = scoreboard.filter(p => !p.isSpectator).filter((_, i) => i % 4 === 0);
    const bottomPlayers = scoreboard.filter(p => !p.isSpectator).filter((_, i) => i % 4 !== 0);

    return (
        <div className="gameview-wrapper">
            {showFinalScreen ? (
                isMobile ? (
                    <QuestionGameSummaryMobile
                        scoreboard={scoreboard.filter(p => !p.isSpectator)}
                        handleLeaveLobby={handleLeaveLobby}
                        handleReturnToLobby={handleReturnToLobby}
                    />
                ) : (
                    <QuestionGameSummary
                        scoreboard={scoreboard.filter(p => !p.isSpectator)}
                        questions={questions}
                        handleLeaveLobby={handleLeaveLobby}
                        handleReturnToLobby={handleReturnToLobby}
                    />
                )
            ) : (
                <>
                    <Countdown timeLeft={timeLeft} progress={progress} />
                    <div className="question-progress">
                        Frage {currentIndex + 1} / {questions.length}
                    </div>

                    <Scoreboard players={topPlayers} revealed={revealed} correct={correct} layout="top" />

                    <div className="game-area">
                        <div className={`question-container ${timeLeft <= 3 && timeLeft > 0 ? 'warning' : ''}`}>
                            <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${progress}%` }} />
                            </div>
                            <QuestionRenderer question={questions[currentIndex]} />
                            {isSpectator && <div className="spectator-badge">Zuschauer-Modus</div>}
                            <AnswerGrid
                                options={questions[currentIndex].options}
                                selected={selected}
                                correct={correct}
                                revealed={revealed}
                                onSelect={selectAnswer}
                                isSpectator={isSpectator}
                                timeLeft={timeLeft}
                            />
                        </div>
                    </div>

                    <Scoreboard players={bottomPlayers} revealed={revealed} correct={correct} layout="bottom" />

                    {timeLeft === 0 && isCreator && !gameOver && (
                        <div className="next-question">
                            <button className="button button-primary" onClick={nextQuestion}>
                                <i className="fas fa-arrow-right" /> Nächste Frage
                            </button>
                        </div>
                    )}

                    {gameOver && (
                        <div className="gameover-actions">
                            <button className="button button-primary" onClick={() => setShowFinalScreen(true)}>
                                <i className="fas fa-arrow-right" /> Zur Übersicht
                            </button>
                            <button className="button button-primary" onClick={handleReturnToLobby}>
                                <i className="fas fa-arrow-left" /> Zurück zur Lobby
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
